/* -----------------------------------
Copyright: Logical Developments 2025.
Project:   ConNote Portal
Filename:  EditVendor.js
Author:    jdkohl
Version:   0.01
Description:
Edit vendor settings - manage authorization status and whitelisted addresses
for a selected vendor. Includes change tracking to detect modifications.

History:
0.01  28-10-25 JDK   Added change detection via useEffect
                      Implemented authorization checkbox handler
                      Implemented address add/remove functionality
0.00  19-08-25 JDK   Created.
----------------------------------- */

import { useEffect } from 'react';
import '../Settings.css'
import CopyCodeCommon from './Common/CopyCodeCommon';
import Permissions from './Common/Permissions';
import Addresses from './Common/Addresses';

function EditVendor({ viewState, dispatchStateChange }) {

  const selectedVendor = viewState?.selectedVendor !== null
    ? viewState.vendors[viewState.selectedVendor]
    : null;

  /**
   * Change detection effect
   * Compares current vendor state with original to detect modifications
   * Updates hasChanges flag when authorization, addresses, or permissions change
   * This enables the Save button and triggers unsaved changes warnings
   */
  useEffect(() => {
    if (selectedVendor && viewState.originalVendor) {
      const hasChanges =
        selectedVendor.authorised !== viewState.originalVendor.authorised ||
        JSON.stringify(selectedVendor.addresses) !== JSON.stringify(viewState.originalVendor.addresses) ||
        JSON.stringify(selectedVendor.permissions) !== JSON.stringify(viewState.originalVendor.permissions);

      if (hasChanges !== viewState.hasChanges) {
        dispatchStateChange({ type: 'hasChanges', value: hasChanges });
      }
    }
  }, [selectedVendor, viewState.originalVendor, viewState.hasChanges, dispatchStateChange]);

  /**
   * Handle address changes from the common Addresses component
   * Updates vendor state with new address list
   */
  const handleAddressesChange = (newAddresses) => {
    if (selectedVendor) {
      const updatedVendor = { ...selectedVendor, addresses: newAddresses };
      const updatedVendors = [...viewState.vendors];
      updatedVendors[viewState.selectedVendor] = updatedVendor;
      dispatchStateChange({ type: 'vendors', value: updatedVendors });
    }
  };

  /**
   * Handle permissions changes from the common Permissions component
   * Updates vendor state with new permissions
   */
  const handlePermissionsChange = (newPermissions) => {
    if (selectedVendor) {
      const updatedVendor = { ...selectedVendor, permissions: newPermissions };
      const updatedVendors = [...viewState.vendors];
      updatedVendors[viewState.selectedVendor] = updatedVendor;
      dispatchStateChange({ type: 'vendors', value: updatedVendors });
    }
  };

  if (!selectedVendor) {
    return <div className="stack">Please select a vendor</div>;
  }

  // Show code if vendor not activated
  if (!selectedVendor.activated) {
    return <CopyCodeCommon title={`Your vendor has not activated their account yet.`} vendor={selectedVendor} />;
  }

  // Show addresses if activated
  return (
    <div className="stack">

      {/* Use common Permissions component */}
      <Permissions
        vendor={selectedVendor}
        onChange={handlePermissionsChange}
      />

      {/* Use common Addresses component */}
      <Addresses
        vendor={selectedVendor}
        myAddresses={viewState.myAddresses}
        onChange={handleAddressesChange}
      />
    </div>
  )
}

export default EditVendor